import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { Organization } from '../../model/organization/organization.model';
import { GoogleMap, MapInfoWindow, MapMarker, MapMarkerClusterer, Renderer } from '@angular/google-maps';
import { AppStateStore } from '../../store/app-state.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { OrganizationMarkerData } from '../../model/organization/organization-marker-data.interface';
import { MatButton } from '@angular/material/button';
import { Platform } from '@angular/cdk/platform';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NavigationOptions } from '../navigation-options/navigation-options';
import { AsyncPipe, NgClass } from '@angular/common';
import { ResponsiveService } from '../../services/responsive.service';
import { getGoalColor } from '../../util/goal-color.util';

@Component({
  selector: 'app-organization-map',
  imports: [
    GoogleMap,
    MapMarker,
    MapInfoWindow,
    MapMarkerClusterer,
    MatButton,
    RouterLink,
    AsyncPipe,
    NgClass,
  ],
  templateUrl: './organization-map.html',
  styleUrl: './organization-map.scss',
})
export class OrganizationMap {
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  @ViewChild(GoogleMap) map!: GoogleMap;
  /**
   * Dependencies
   */
  private readonly appStateStore = inject(AppStateStore);
  private readonly destroyRef = inject(DestroyRef)
  private readonly matBottomSheet = inject(MatBottomSheet);
  public readonly platform = inject(Platform);
  public readonly responsiveService = inject(ResponsiveService);
  /**
   * Data
   */
  public markerList: OrganizationMarkerData[] = [];
  public infoWindowOrganization: Organization = Organization.createFromApi();
  /**
   * Geolocation
   */
  public userLocation: google.maps.LatLngLiteral | null = null;
  public userLocationMarker: google.maps.MarkerOptions | null = null;
  /**
   * Map
   */
  public mapCenter = {lat: 52.237, lng: 5.279}; // Rotterdam
  public mapOptions: google.maps.MapOptions = {
    center: this.mapCenter,
    zoom: 8,
    mapTypeControl: false,
    fullscreenControl: true,
    streetViewControl: false,
    gestureHandling: "greedy",
    backgroundColor: "#F9CDD9",
    // Styling is managed in the Google Cloud Console and applied via this Map ID.
    // When a mapId is set, any inline `styles` array is ignored by Google Maps,
    // so the styling lives in the cloud against this id (map name: "Netwerk Levend Erfgoed").
    mapId: "4ca6a7959978df317f8fcb29",
  };

  onMapReady() {
    this.listenToOrganizationChanges();
    this.requestGeolocation();
  }

  private listenToOrganizationChanges(): void {
    this.appStateStore.organizationList$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((organizationList: Organization[]): void => {
        this.markerList = organizationList.map((organization: Organization) => ({
          organization: organization,
          position: {
            lat: parseFloat(organization.latitude),
            lng: parseFloat(organization.longitude),
          },
          options: {
            title: organization.name,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: getGoalColor(organization.goalList[0]?.class ?? '').color,
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
            },
          },
        }));
        this.adjustViewportForRegion();
      });
  }

  // Longitudes west of this belong to the Dutch Caribbean; the Netherlands is well east of it.
  private static readonly CARIBBEAN_LNG_THRESHOLD = -20;

  private isCaribbean(position: google.maps.LatLngLiteral): boolean {
    return position.lng < OrganizationMap.CARIBBEAN_LNG_THRESHOLD;
  }

  private isValidPosition(position: google.maps.LatLngLiteral): boolean {
    return Number.isFinite(position.lat) && Number.isFinite(position.lng);
  }

  /**
   * When a filter narrows results down to only Dutch-Caribbean initiatives, frame those
   * markers. For any result that still includes the Netherlands (NL-only or mixed), keep the
   * default Netherlands viewport.
   */
  private adjustViewportForRegion(): void {
    const map = this.map?.googleMap;
    if (!map) {
      return;
    }

    const validMarkers = this.markerList.filter((marker: OrganizationMarkerData) => this.isValidPosition(marker.position));
    if (validMarkers.length === 0) {
      return;
    }

    const caribbeanMarkers = validMarkers.filter((marker: OrganizationMarkerData) => this.isCaribbean(marker.position));
    const isCaribbeanOnly = caribbeanMarkers.length === validMarkers.length;

    if (!isCaribbeanOnly) {
      // Mixed or NL-only → restore the default Netherlands view.
      map.setCenter(this.mapCenter);
      map.setZoom(8);
      return;
    }

    // Caribbean-only → frame the Caribbean markers.
    if (caribbeanMarkers.length === 1) {
      map.setCenter(caribbeanMarkers[0].position);
      map.setZoom(11);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    caribbeanMarkers.forEach((marker: OrganizationMarkerData) => bounds.extend(marker.position));
    map.fitBounds(bounds, {top: 50, bottom: 50, left: 50, right: 50});
  }

  // Custom cluster rendering (color + size based on marker count)
  public customClusterRenderer: Renderer = {
    render: ({count, position}) => {
      let color = '#0080f7ff';
      let size = 36;

      if (count >= 7) {
        color = '#F59E0B';
        size = 42;
      }

      if (count >= 15) {
        color = '#EF4444';
        size = 46;
      }

      color = '#f22e89';

      return new google.maps.Marker({
        position,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: size / 3,
          fillColor: color,
          fillOpacity: 0.85,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
        label: {
          text: String(count),
          color: 'white',
          fontSize: '14px',
          fontWeight: '700',
        },
        title: `Cluster of ${count} markers`,
        zIndex: 1000 + count,
      });
    },
  };

  public openInfoWindow(marker: MapMarker, markerData: OrganizationMarkerData): void {
    this.infoWindowOrganization = markerData.organization;
    this.infoWindow.open(marker);
  }

  public closeInfoWindow(): void {
    this.infoWindow.close();
  }

  private requestGeolocation(): void {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.userLocationMarker = {
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            },
          };
        },
        (error) => {
          console.log('Geolocation permission denied or error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      );
    }
  }

  public openNavigationOptions(): void {
    this.matBottomSheet.open(
      NavigationOptions,
      {
        data: {lat: this.infoWindowOrganization.latitude, lng: this.infoWindowOrganization.longitude},
      });
  }

}
